/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import React, {ForwardedRef, forwardRef, useContext, useEffect, useImperativeHandle, useRef, useState} from 'react';
import {AppContext} from '../../../misc/appContext';
import {importVats} from '../../../../util/requestSender';
import {ErrorType, ResponseData} from '@dvrd/dvr-controls';
import {ActivateHandle} from '../../../../util/interfaces';
import {createErrorMessage, downloadFileURL} from '../../../../util/utils';
import ImportVat from './importVat';
import {showDialog, showSnackbar} from '@dvrd/dvr-controls';
import {useTranslation} from 'react-i18next';

interface Props {
    onSuccess: VoidFunction;
}

function ImportVatController(props: Props, ref: ForwardedRef<ActivateHandle>) {
    const context = useContext(AppContext);
    const {t} = useTranslation();
    const {onSuccess} = props;
    const [importFile, setImportFile] = useState<File | null>(null);
    const [loading, setLoading] = useState(false);
    const [active, setActive] = useState(false);
    const [error, setError] = useState<ErrorType>(null);
    const [applyRetroactive, setApplyRetroactive] = useState(false);
    const errorFile = useRef<string | null>(null);

    function onDownloadErrorFile() {
        if (!errorFile.current) return;

        const link = document.createElement('a');
        link.href = errorFile.current;
        link.download = 'import-fouten.xlsx';
        link.click();
    }

    function onActivate() {
        setActive(true);
    }

    function onClose() {
        setActive(false);
        setImportFile(null);
        setLoading(false);
        setError(null);
        if (errorFile.current) URL.revokeObjectURL(errorFile.current);
        errorFile.current = null;
    }

    function onChangeApplyRetroactive() {
        setApplyRetroactive(!applyRetroactive);
    }

    function onDownloadTemplate() {
        const fileURL = 'https://app.bolmate.nl/static/btw-import-template-v2.xlsx';
        downloadFileURL(fileURL, 'bolmate-btw-import-template.xlsx');
    }

    function onChangeFile(evt: React.ChangeEvent<HTMLInputElement>) {
        const {files} = evt.target;
        if (!files) return;
        setImportFile(files[0]);
    }

    function onSubmit() {
        const {account} = context.accountContext;
        if (!account || !importFile) return;
        if (!importFile.name.toLowerCase().endsWith('.xlsx'))
            return showDialog(
                t(
                    'settings.vat.onlyXlsxAccepted',
                    'We accepteren alleen .xlsx bestanden, gebruik het voorbeeldbestand.'
                )
            );
        setLoading(true);
        importVats({
            file: importFile,
            account_id: account.id,
            create_non_existing: false,
            apply_retroactive: applyRetroactive,
            callback: (data: ResponseData, response: Response) => {
                setLoading(false);
                if (response.headers.get('Content-Type') === 'application/json') {
                    if (data.success) {
                        const {num_imported} = data;
                        const countLabel =
                            num_imported === 1 ? t('settings.vat.row', 'regel') : t('settings.vat.rows', 'regels');
                        onSuccess();
                        onClose();
                        showSnackbar(
                            t('settings.vat.importSuccess', 'Succesvol {{num}} {{label}} bijgewerkt', {
                                num: num_imported,
                                label: countLabel
                            })
                        );
                    } else
                        showDialog(
                            createErrorMessage(
                                data.message ??
                                    t(
                                        'settings.vat.importFailedMsg',
                                        'Het importeren van de BTW percentages is niet gelukt.'
                                    )
                            ),
                            t('settings.vat.importFailedTitle', 'Importeren mislukt')
                        );
                } else if (data.status === 'success') {
                    response.blob().then((blob: Blob) => {
                        const numError = Number(response.headers.get('X-Num-Error') ?? 0);
                        const numImported = Number(response.headers.get('X-Num-Imported') ?? 0);
                        // Revoke old URL before creating a new one
                        if (errorFile.current) URL.revokeObjectURL(errorFile.current);
                        errorFile.current = URL.createObjectURL(blob);
                        setError(
                            t(
                                'settings.vat.partialImportError',
                                '{{imported}} regels zijn geïmporteerd, maar {{errors}} kon{{suffix}} niet geïmporteerd worden. Zie het bestand hieronder voor de details.',
                                {imported: numImported, errors: numError, suffix: numError === 1 ? '' : 'den'}
                            )
                        );
                    });
                }
            }
        });
    }

    useEffect(() => {
        return () => {
            if (errorFile.current) URL.revokeObjectURL(errorFile.current);
        };
    }, []);

    useImperativeHandle(ref, () => ({
        activate: onActivate
    }));

    return (
        <ImportVat
            onClose={onClose}
            onDownloadTemplate={onDownloadTemplate}
            onChangeFile={onChangeFile}
            onSubmit={onSubmit}
            onDownloadErrorFile={onDownloadErrorFile}
            onChangeApplyRetroactive={onChangeApplyRetroactive}
            importFile={importFile}
            loading={loading}
            active={active}
            error={error}
            applyRetroactive={applyRetroactive}
        />
    );
}

export default forwardRef<ActivateHandle, Props>(ImportVatController);
